import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider, useMsal } from '@azure/msal-react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import React, { createContext, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { msalConfig } from '../apis/azureLogin/configuration'
import { setAccessToken as setAxiosToken } from '../apis/moovyAxiosClient'
import {
  AZURE_APPLICATION_CLIENT_ID,
  AZURE_APPLICATION_SCOPE,
} from '../envConfig'
import { MoovyToast } from '../utils/moovyToast'
import { getShortAvatarName } from '../utils/utils'

// In order to make the application less tied to a certain authentication
// provider (such as Azure AD), we provide an application specific
// AuthenticationProvider that can be used as a proxy to the actual
// authentication provider. This way, the application can be more easily
// migrated to another authentication provider by changing the implementation
// of the AuthenticationProvider, without changing the rest of the application.

interface AuthenticationContextType {
  isAuthenticated: boolean
  isAdmin: boolean
  isReady: boolean
  hasConfigurationError: boolean
  accessToken: string | null
  login: () => void
  logout: () => void
  userFullName: string
  usernameForAvatar: string
}

export const AuthenticationContext = createContext<AuthenticationContextType>({
  isAuthenticated: false,
  isAdmin: false,
  isReady: false,
  hasConfigurationError: false,
  accessToken: null,
  login: () => {},
  logout: () => {},
  userFullName: '',
  usernameForAvatar: '',
})

const msalInstance = new PublicClientApplication(msalConfig)

const AuthenticationProvider = (props: React.PropsWithChildren) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticationProviderWrapper>
        {props.children}
      </AuthenticationProviderWrapper>
    </MsalProvider>
  )
}

const AuthenticationProviderWrapper = (props: React.PropsWithChildren) => {
  const [accessToken, setAccessTokenState] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  const { instance } = useMsal()
  let refreshTokenTimeout: NodeJS.Timeout | null = null
  const clearRefreshTokenTimeout = () => {
    if (refreshTokenTimeout) {
      clearTimeout(refreshTokenTimeout)
      refreshTokenTimeout = null
    }
  }

  // Cleanup the refresh token timeout when the component is unmounted
  useEffect(() => {
    return () => {
      clearRefreshTokenTimeout()
    }
  }, [])

  // MSAL instance initialization
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize()
        await updateAccessToken()
      } catch (error) {
        MoovyToast.showToast(
          <FormattedMessage id="login.toast.error" />,
          'error',
        )
      }
    }
    initializeMsal()
  }, [])

  // Update both Axios and internal state when the access token changes
  const setAccessToken = (accessToken: string | null) => {
    setAxiosToken(accessToken)
    setAccessTokenState(accessToken)
  }

  const updateAccessToken = async () => {
    const activeAccount = instance.getActiveAccount()
    if (!activeAccount) {
      setAccessToken(null)
      setIsReady(true)
      return
    }
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [`${AZURE_APPLICATION_CLIENT_ID}/${AZURE_APPLICATION_SCOPE}`],
        account: activeAccount,
      })
      setAccessToken(tokenResponse.accessToken)

      // Schedule token refresh 1 minute before the token expires
      const refreshBeforeExpirationMs = 1 * 60 * 1000
      const currentDate = new Date()
      // If the token does not have an expiration date, set it to 45 minutes from now
      const tokenExpiration =
        tokenResponse.expiresOn ||
        new Date(currentDate.getTime() + 1000 * 60 * 45)
      const tokenRefreshMs =
        tokenExpiration.getTime() -
        currentDate.getTime() -
        refreshBeforeExpirationMs
      clearRefreshTokenTimeout()
      refreshTokenTimeout = setTimeout(updateAccessToken, tokenRefreshMs)
    } catch (error) {
      MoovyToast.showToast(<FormattedMessage id="login.toast.error" />, 'error')
      logout()
    } finally {
      setIsReady(true)
    }
  }

  const login = async () => {
    setIsReady(false)
    try {
      const response = await instance.loginPopup()
      instance.setActiveAccount(response.account)
      // updateAccessToken sets isReady to true after getting the token
      await updateAccessToken()
    } catch (error) {
      setIsReady(true)
      MoovyToast.showToast(<FormattedMessage id="login.toast.error" />, 'error')
    }
  }

  const logout = async () => {
    setIsReady(false)
    clearRefreshTokenTimeout()
    try {
      await instance.logoutPopup()
      setAccessToken(null)
      setIsReady(true)
    } catch (error) {
      MoovyToast.showToast(
        <FormattedMessage id="logout.toast.error" />,
        'error',
      )
    } finally {
      setIsReady(true)
    }
  }

  const isAdmin = !!instance
    .getActiveAccount()
    ?.idTokenClaims?.roles?.includes('Admin')
  const userFullName = instance.getActiveAccount()?.name || ''

  // Show loading spinner while the authentication state is being determined
  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  // Only render the children when the authentication setup is complete
  return (
    <AuthenticationContext.Provider
      value={{
        isAuthenticated: !!accessToken,
        isAdmin,
        isReady,
        hasConfigurationError: !msalConfig.isConfiguredProperly,
        accessToken,
        login,
        logout,
        userFullName,
        usernameForAvatar: getShortAvatarName(userFullName),
      }}
    >
      {props.children}
    </AuthenticationContext.Provider>
  )
}

export default AuthenticationProvider
